import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, Trophy, Star, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { ACHIEVEMENTS_PAGE } from "../config/content";
import Header from "./Header";
import Footer from "./Footer";
import PageSEO from "./PageSEO";

export default function AchievementsPage() {
  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Achievements & Awards"
        description="Awards and recognition received by Shree Siddhivinayak Enterprise from leading brands like Sartorius, PerkinElmer, Glassco and Equitron Medica."
        path="/achievements"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="overline text-[#0047FF] mb-4">Our Achievements</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6">
              {ACHIEVEMENTS_PAGE.hero.title}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {ACHIEVEMENTS_PAGE.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Achievement Image */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <img 
              src={ACHIEVEMENTS_PAGE.achievementImage}
              alt="Our Achievements"
              className="w-full max-w-3xl mx-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Awards Timeline */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline text-[#0047FF] mb-4">Awards & Recognition</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Our Journey of Excellence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ACHIEVEMENTS_PAGE.awards.map((award, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white border border-slate-200 p-6 hover:border-[#0047FF] hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#0047FF] text-white flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-[#0047FF]">{award.year}</span>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">
                      {award.title}
                    </h3>
                    <p className="text-slate-600 text-sm mt-2">
                      {award.description}
                    </p>
                    <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                      <Star className="w-3 h-3" /> {award.brand}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0047FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            <div>
              <p className="text-4xl md:text-5xl font-black font-heading">{ACHIEVEMENTS_PAGE.awards.length}+</p>
              <p className="text-sm opacity-80 mt-2">Awards Won</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black font-heading">15+</p>
              <p className="text-sm opacity-80 mt-2">Years of Excellence</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black font-heading">17+</p>
              <p className="text-sm opacity-80 mt-2">Brand Partners</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black font-heading">500+</p>
              <p className="text-sm opacity-80 mt-2">Happy Clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Partner with an Award-Winning Team
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Experience the same quality and service that earned us recognition from leading brands.
          </p>
          <Link to="/contact">
            <Button className="bg-[#0047FF] text-white hover:bg-[#0037CC] rounded-none px-8 py-6 text-base">
              Get In Touch <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
